import { tsMs } from './tts-debug';

/**
 * Ensures only one page is running read-along at a time, newest wins.
 *
 * Needed because of the audio-session fix, not despite it. Declaring
 * `navigator.audioSession.type = 'playback'` and holding the keep-warm
 * deliberately stops iOS suspending a backgrounded page's audio — that is what
 * fixed the screen-lock and app-switch clipping (MIC-652). The side effect is
 * that a page the guest has finished with no longer falls silent on its own:
 * open the join link twice, or reopen it from history, and two pages stream and
 * play at once. Observed on dev as en-US and en-GB audible simultaneously,
 * from two live guest subscriptions.
 *
 * That is worse than the clipping it fixes, so the newest page claims
 * ownership and any older one tears its read-along down. A superseded page
 * does not try to reclaim when the owner goes away: the page the guest is
 * actually looking at is the one that just claimed, and silently resurrecting
 * audio in a backgrounded tab is the behaviour being fixed.
 */
const OWNER_KEY = 'ttsReadAlongOwner';
const CHANNEL_NAME = 'tts-read-along-owner';

interface Claim {
  id: string;
  at: number;
}

export class TtsInstanceLock {
  private readonly id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  private channel: BroadcastChannel | null = null;
  private supersededCb: (() => void) | null = null;
  private claimedAt = 0;
  private claimed = false;

  constructor(private readonly document: Document) {}

  /** Called when a newer page takes over. Fired at most once per claim. */
  onSuperseded(cb: () => void): void {
    this.supersededCb = cb;
  }

  isOwner(): boolean {
    return this.claimed;
  }

  claim(): void {
    this.claimedAt = Date.now();
    this.claimed = true;
    this.listen();
    const claim: Claim = { id: this.id, at: this.claimedAt };
    try {
      localStorage.setItem(OWNER_KEY, JSON.stringify(claim));
    } catch {
      /* private mode — fall back to the channel alone */
    }
    try {
      this.channel?.postMessage(claim);
    } catch {
      /* channel closed */
    }
    console.log(`[TTS ${tsMs()}] instance lock claimed by ${this.id}`);
  }

  release(): void {
    this.claimed = false;
    // Only clear the record if it is still ours; a newer page's claim must
    // survive our teardown.
    try {
      const raw = localStorage.getItem(OWNER_KEY);
      if (raw !== null && (JSON.parse(raw) as Claim).id === this.id) {
        localStorage.removeItem(OWNER_KEY);
      }
    } catch {
      /* unreadable or private mode */
    }
    this.stopListening();
  }

  private listen(): void {
    if (this.channel) {
      return;
    }
    try {
      if (typeof BroadcastChannel === 'function') {
        this.channel = new BroadcastChannel(CHANNEL_NAME);
        this.channel.addEventListener('message', this.onMessage);
      }
    } catch {
      /* unsupported — the storage event is the fallback */
    }
    this.document.defaultView?.addEventListener('storage', this.onStorage);
  }

  private stopListening(): void {
    try {
      this.channel?.removeEventListener('message', this.onMessage);
      this.channel?.close();
    } catch {
      /* already closed */
    }
    this.channel = null;
    this.document.defaultView?.removeEventListener('storage', this.onStorage);
  }

  private readonly onMessage = (event: MessageEvent): void => {
    this.consider(event.data as Claim | null);
  };

  private readonly onStorage = (event: StorageEvent): void => {
    if (event.key !== OWNER_KEY || event.newValue === null) {
      return;
    }
    try {
      this.consider(JSON.parse(event.newValue) as Claim);
    } catch {
      /* malformed */
    }
  };

  private consider(claim: Claim | null): void {
    if (!this.claimed || !claim || typeof claim.id !== 'string') {
      return;
    }
    if (claim.id === this.id) {
      return;
    }
    // Ties broken by id so two pages claiming in the same millisecond still
    // agree on which one wins, rather than both standing down or both staying.
    const newer = claim.at > this.claimedAt
      || (claim.at === this.claimedAt && claim.id > this.id);
    if (!newer) {
      return;
    }
    this.claimed = false;
    this.stopListening();
    console.log(
      `[TTS ${tsMs()}] instance lock superseded by ${claim.id}, stopping read-along here`,
    );
    this.supersededCb?.();
  }
}
